import { createI18n } from 'vue-i18n'
import { slavicPlural, polishPlural } from './libs/pluralization'

function loadLocaleMessages () {
  const localeFiles = import.meta.glob('./locales/*.json', { eager: true, import: 'default' })
  const messages = {}
  for (const path in localeFiles) {
    const match = path.match(/([A-Za-z0-9-_]+)\.json$/i)
    if (match && match[1]) {
      messages[match[1]] = localeFiles[path]
    }
  }
  return messages
}

const i18n = createI18n({
  legacy: true,
  locale: import.meta.env.VITE_I18N_LOCALE || 'en',
  fallbackLocale: import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'en',
  messages: loadLocaleMessages(),
  pluralizationRules: {
    cs: slavicPlural,
    sk: slavicPlural,
    pl: polishPlural,
  },
  warnHtmlMessage: false,
  missingWarn: false,
  fallbackWarn: false,
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  missing: (locale, key) => key,
  messageResolver (messages, key) {
    if (messages == null) return null
    return messages[key] ?? null
  },
})

// vue-i18n 9 throws INVALID_ARGUMENT when the translate key is not a string, whereas
// vue-i18n 8 tolerated null/undefined (returning ''). The app has many $t(dynamicValue)
// calls whose value can be null (currency, gender, skillLevel, priceListType, ...); under
// v9 a single null key thrown mid-render blanks the whole surrounding view (e.g. the cart).
// Restore the forgiving v8 behavior by coercing non-string keys on the legacy global
// translate functions that $t / $tc / $te delegate to.
function guardKey (fn, ctx) {
  return function (key, ...args) {
    if (typeof key !== 'string') {
      return key == null ? '' : String(key)
    }
    return fn.call(ctx, key, ...args)
  }
}
const g = i18n.global
if (g) {
  if (typeof g.t === 'function') g.t = guardKey(g.t, g)
  if (typeof g.tc === 'function') g.tc = guardKey(g.tc, g)
  if (typeof g.te === 'function') {
    const origTe = g.te
    g.te = function (key, ...args) {
      if (typeof key !== 'string') return false
      return origTe.call(g, key, ...args)
    }
  }

  // Legacy compatibility: components import the default i18n and call i18n.t(...)
  // directly (vue-i18n 8 exposed .t/.tc/.te on the instance; v9 moved them to
  // i18n.global). Delegate to the guarded global functions above.
  i18n.t = (...args) => g.t(...args)
  i18n.tc = (...args) => g.tc(...args)
  i18n.te = (...args) => g.te(...args)
}

export default i18n
